import React from 'react';
import { ShoppingCart, MessageCircle, Search, FileText, Package, Calendar, Users, BookOpen, AlertTriangle } from 'lucide-react';

// Empty Cart State
export const EmptyCart: React.FC<{
  onContinueShopping: () => void;
}> = ({ onContinueShopping }) => (
  <div className="text-center py-12 animate-fade-in">
    <ShoppingCart className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h4>
    <p className="text-gray-600 mb-6">Add some products to get started</p>
    <button
      onClick={onContinueShopping}
      className="btn-primary"
    >
      Continue Shopping
    </button>
  </div>
);

// Empty Conversations State
export const EmptyConversations: React.FC<{
  onStartConversation: () => void;
}> = ({ onStartConversation }) => (
  <div className="text-center py-12 animate-fade-in">
    <MessageCircle className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No conversations yet</h4>
    <p className="text-gray-600 mb-6">Start a conversation with an expert or community member</p>
    <button
      onClick={onStartConversation}
      className="btn-primary"
    >
      Start a Conversation
    </button>
  </div>
);

// No Search Results State
export const NoSearchResults: React.FC<{
  searchTerm: string;
  onClearSearch: () => void;
  entityType: string;
}> = ({ searchTerm, onClearSearch, entityType }) => (
  <div className="text-center py-12 animate-fade-in">
    <Search className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No {entityType} found</h4>
    <p className="text-gray-600 mb-6">
      We couldn't find any {entityType} matching "{searchTerm}"
    </p>
    <button
      onClick={onClearSearch}
      className="btn-primary"
    >
      Clear Search
    </button>
  </div>
);

// Empty Orders State
export const EmptyOrders: React.FC<{
  onShopNow: () => void;
}> = ({ onShopNow }) => (
  <div className="text-center py-12 animate-fade-in">
    <Package className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h4>
    <p className="text-gray-600 mb-6">Your order history will appear here</p>
    <button
      onClick={onShopNow}
      className="btn-primary"
    >
      Shop Now
    </button>
  </div>
);

// Empty Prescriptions State
export const EmptyPrescriptions: React.FC<{
  onUploadPrescription: () => void;
}> = ({ onUploadPrescription }) => (
  <div className="text-center py-12 animate-fade-in">
    <FileText className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No prescriptions yet</h4>
    <p className="text-gray-600 mb-6">Upload a prescription to order prescription medications</p>
    <button
      onClick={onUploadPrescription}
      className="btn-primary"
    >
      Upload Prescription
    </button>
  </div>
);

// No Upcoming Sessions State
export const NoUpcomingSessions: React.FC<{
  onBrowseSessions: () => void;
}> = ({ onBrowseSessions }) => (
  <div className="text-center py-12 animate-fade-in">
    <Calendar className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No upcoming sessions</h4>
    <p className="text-gray-600 mb-6">Check back later or browse all available sessions</p>
    <button
      onClick={onBrowseSessions}
      className="btn-primary"
    >
      Browse Sessions
    </button>
  </div>
);

// Empty Community State
export const EmptyCommunity: React.FC<{
  onCreatePost: () => void;
}> = ({ onCreatePost }) => (
  <div className="text-center py-12 animate-fade-in">
    <Users className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No discussions yet</h4>
    <p className="text-gray-600 mb-6">Be the first to start a conversation!</p>
    <button
      onClick={onCreatePost}
      className="btn-primary"
    >
      Start Discussion
    </button>
  </div>
);

// No Articles State
export const NoArticles: React.FC<{
  onBrowseCategories: () => void;
}> = ({ onBrowseCategories }) => (
  <div className="text-center py-12 animate-fade-in">
    <BookOpen className="mx-auto text-gray-400 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No articles found</h4>
    <p className="text-gray-600 mb-6">Try browsing different categories or check back later</p>
    <button
      onClick={onBrowseCategories}
      className="btn-primary"
    >
      Browse Categories
    </button>
  </div>
);

// No Internet Connection State
export const NoInternetConnection: React.FC<{
  onRetry: () => void;
}> = ({ onRetry }) => (
  <div className="text-center py-12 animate-fade-in">
    <AlertTriangle className="mx-auto text-amber-500 mb-4" size={64} />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No Internet Connection</h4>
    <p className="text-gray-600 mb-6">Please check your connection and try again</p>
    <button
      onClick={onRetry}
      className="btn-primary"
    >
      Retry Connection
    </button>
  </div>
);

// Generic Empty State
export const GenericEmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ icon, title, description, actionLabel, onAction }) => (
  <div className="text-center py-12 animate-fade-in">
    <div className="mx-auto text-gray-400 mb-4">
      {icon}
    </div>
    <h4 className="text-lg font-medium text-gray-900 mb-2">{title}</h4>
    <p className="text-gray-600 mb-6">{description}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="btn-primary"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// Export the EmptyState component as an alias for GenericEmptyState for backward compatibility
export const EmptyState = GenericEmptyState;